import { AlertTriangle, Minus, Zap, type LucideIcon } from "lucide-react";

export type ImpactLevel = "Low" | "Medium" | "High";

export const impactLucideIcons: Record<ImpactLevel, LucideIcon> = {
  High: Zap,
  Medium: AlertTriangle,
  Low: Minus,
};

/** Emphasized filter button when impact filter is toggled active */
export function impactFilterChipActiveClass(impact: ImpactLevel): string {
  switch (impact) {
    case "High":
      return "bg-destructive text-destructive-foreground border-transparent hover:bg-destructive/90";
    case "Medium":
      return "border-transparent bg-[hsl(var(--chart-4)/0.22)] text-foreground hover:bg-[hsl(var(--chart-4)/0.3)] ring-1 ring-[hsl(var(--chart-4)/0.4)]";
    case "Low":
      return "bg-muted text-muted-foreground border-transparent hover:bg-muted/80 ring-1 ring-border";
    default:
      return "";
  }
}

/** Compact Badge / pill for list rows (token-friendly, avoids raw palette) */
export function impactBadgeClassName(impact: ImpactLevel): string {
  switch (impact) {
    case "High":
      return "border-destructive/40 bg-destructive/15 text-destructive";
    case "Medium":
      return "border-[hsl(var(--chart-4)/0.35)] bg-[hsl(var(--chart-4)/0.14)] text-foreground";
    case "Low":
      return "border-muted-foreground/25 bg-muted/50 text-muted-foreground";
    default:
      return "";
  }
}

export type SentimentTone = "up" | "down" | "flat" | "unknown";

export function sentimentVsForecast(actual: string, forecast: string): SentimentTone {
  if (!actual?.trim() || !forecast?.trim()) return "unknown";
  const a = parseFloat(actual.replace(/,/g, ""));
  const f = parseFloat(forecast.replace(/,/g, ""));
  if (!Number.isFinite(a) || !Number.isFinite(f)) return "unknown";
  if (a > f) return "up";
  if (a < f) return "down";
  return "flat";
}

/** Icon tint for trending vs forecast */
export function sentimentIconClass(tone: SentimentTone): string {
  switch (tone) {
    case "up":
      return "text-chart-2";
    case "down":
      return "text-destructive";
    default:
      return "text-muted-foreground";
  }
}

/** Numeric / label tint next to sentiment icon */
export function sentimentTextClass(tone: SentimentTone): string {
  switch (tone) {
    case "up":
      return "text-chart-2 font-semibold";
    case "down":
      return "text-destructive font-semibold";
    default:
      return "text-foreground";
  }
}

export function statsCountHighlightClass(
  tier: ImpactLevel | "total"
): string {
  switch (tier) {
    case "High":
      return "text-destructive";
    case "Medium":
      return "text-chart-4";
    case "Low":
      return "text-muted-foreground";
    case "total":
      return "text-primary";
    default:
      return "";
  }
}
